import {
  BackgroundColors,
  PositiveColors,
  TextColors,
} from '@picsart/design-system/foundation/colors';
import { FontSizes, LineHeights } from '@picsart/design-system/foundation/typography';

import { FontWeights } from '@picsart/design-system/foundation/fonts';
import { CornerRadius } from '@picsart/design-system/foundation/cornerRadius';
import { Spacings } from '@picsart/design-system/foundation/spacings';
import { mediaQuery } from '@picsart/rc/helpers';
import { ignoreRTL } from '@picsart/design-system/foundation/styleHelpers';
import { createUseStyles } from 'react-jss';

import type { layoutType } from './types';

interface propsType {
  type: layoutType;
}

type classNamesList =
  | 'root'
  | 'title'
  | 'wrapper'
  | 'summaryList'
  | 'skeletonTitle'
  | 'skeletonWrapper'
  | 'skeletonDescription'
  | 'descriptionContainer'
  | 'skeletonSummaryList';

const useStyles = createUseStyles<classNamesList, propsType>(
  () => ({
    root: {
      padding: [Spacings.s64, Spacings.s0],

      [mediaQuery(1365)]: {
        padding: [Spacings.s40, Spacings.s0],
      },

      [mediaQuery(1023)]: {
        padding: [Spacings.s64, Spacings.s0],
      },

      // [mediaQuery(767)]: {
      '@media screen and (max-width: 767px)': {
        padding: [Spacings.s40, Spacings.s0],
      },
    },
    wrapper: {
      borderRadius: CornerRadius.r16,
      border: `1px solid ${BackgroundColors.tint3}`,
      flexDirection: 'column',
      boxSizing: 'border-box',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
    },
    title: {
      position: 'absolute',
      bottom: 'calc(100% - 18px)',
      lineHeight: LineHeights.l48,
      padding: [Spacings.s0, Spacings.s16],
      fontSize: FontSizes.s36,
      backgroundColor: PositiveColors.base.default,
    },
    descriptionContainer: {
      maxWidth: 736,
      marginTop: Spacings.s24,
      marginBottom: Spacings.s40,
      padding: [Spacings.s0, Spacings.s16],
      '& > p': {
        fontStyle: FontWeights.normal,
        fontSize: FontSizes.s16,
        lineHeight: LineHeights.l24,
        textAlign: 'center',
        color: TextColors.tint1,
      },
    },
    summaryList: {
      listStyle: 'none',
      display: 'grid',
      gridGap: Spacings.s24,
      padding: [Spacings.s0, Spacings.s32, Spacings.s32],
      gridTemplateColumns: ({ type }) =>
        type === 'twoColumnLayout' ? 'repeat(2, 6fr)' : 'repeat(3, 6fr)',
      // [mediaQuery(766)]: {
      '@media screen and (max-width: 766px)': {
        gridGap: Spacings.s12,
      },
      // [mediaQuery(767)]: {
      '@media screen and (max-width: 767px)': {
        gridTemplateColumns: ({ type }) => type === 'twoColumnLayout' && '12fr',
      },
      [mediaQuery(1023)]: {
        gridTemplateColumns: ({ type }) => type !== 'twoColumnLayout' && '12fr',
      },
    },
    // Skeleton
    skeletonTitle: {
      extend: 'title',
      width: '100%',
      maxWidth: 400,
      height: 40,
    },
    skeletonWrapper: {
      extend: 'wrapper',
      width: '100%',
      height: ({ type }) => (type === 'twoColumnLayout' ? 436 : 300),
      [mediaQuery(1023)]: {
        [ignoreRTL]: {
          height: 500,
        },
      },
    },
    skeletonDescription: {
      extend: 'descriptionContainer',
      width: '100%',
      maxWidth: 550,
      height: 40,
      marginTop: Spacings.s32,
    },
    skeletonSummaryList: {
      width: '100%',
      height: ({ type }) => (type === 'twoColumnLayout' ? 332 : 250),
      [mediaQuery(1023)]: {
        [ignoreRTL]: {
          height: '100%',
        },
      },
    },
  }),
  {
    name: 'blocks-on-background',
  },
);

export default useStyles;
